"use client"

import { Popover } from "@base-ui/react/popover"
import { QuestionMarkCircleIcon } from "@heroicons/react/16/solid"
import { LockSolidCheckIcon, LockSolidIcon } from "@src/icons"
import { useConfidentialMode } from "@src/providers/ConfidentialModeProvider"
import clsx from "clsx"
import { useTheme } from "next-themes"
import { useCallback, useEffect, useRef, useState } from "react"
import Button from "./Button"
import Spinner from "./Spinner"

const DISMISS_MS = 4000

const ConfidentialToggleMobile = () => {
  const { resolvedTheme } = useTheme()
  const { confidentialMode, setConfidentialMode, isAuthenticating, openModal } =
    useConfidentialMode()

  const [popoverOpen, setPopoverOpen] = useState(false)
  const pendingEnable = useRef(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (confidentialMode && pendingEnable.current) {
      pendingEnable.current = false
      setPopoverOpen(true)
      const timer = setTimeout(() => setPopoverOpen(false), DISMISS_MS)
      return () => clearTimeout(timer)
    }
  }, [confidentialMode])

  const handleToggle = useCallback(() => {
    if (confidentialMode) {
      setConfidentialMode(false)
    } else {
      pendingEnable.current = true
      setConfidentialMode(true)
    }
  }, [confidentialMode, setConfidentialMode])

  return (
    <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
      <Button
        ref={anchorRef}
        size="lg"
        variant={
          resolvedTheme === "dark"
            ? "secondary"
            : confidentialMode
              ? "primary"
              : "secondary"
        }
        onClick={handleToggle}
        className="size-10!"
      >
        <span className="sr-only">
          {confidentialMode
            ? "Disable confidential mode"
            : "Enable confidential mode"}
        </span>
        {isAuthenticating ? (
          <Spinner />
        ) : confidentialMode ? (
          <LockSolidCheckIcon className="size-5 text-green-500" />
        ) : (
          <LockSolidIcon className="size-5" />
        )}
      </Button>

      <Popover.Portal>
        <Popover.Positioner
          anchor={anchorRef}
          collisionPadding={8}
          sideOffset={5}
        >
          <Popover.Popup
            className={clsx(
              "flex origin-(--transform-origin) items-center gap-2 rounded-xl bg-gray-700 px-3 py-2 font-semibold text-gray-300 text-sm/5 shadow-xl ring ring-white/5 ring-inset",

              "transition duration-200 ease-out",

              "data-starting-style:-translate-y-1 data-starting-style:scale-95 data-starting-style:opacity-0",

              "data-ending-style:-translate-y-1 data-ending-style:scale-95 data-ending-style:opacity-0 data-ending-style:duration-100 data-ending-style:ease-in"
            )}
          >
            <CountdownCircle />
            Confidential mode on
            <button
              type="button"
              onClick={openModal}
              className="relative flex size-6 shrink-0 items-center justify-center"
            >
              <span
                className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 pointer-fine:hidden size-9"
                aria-hidden="true"
              />
              <QuestionMarkCircleIcon className="relative size-5 shrink-0 text-gray-300" />
            </button>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default ConfidentialToggleMobile

const CIRCLE_R = 6
const CIRCLE_C = 2 * Math.PI * CIRCLE_R

const CountdownCircle = () => (
  <svg
    className="-rotate-90 size-5 shrink-0"
    viewBox="0 0 16 16"
    aria-hidden="true"
  >
    <circle
      cx="8"
      cy="8"
      r={CIRCLE_R}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-gray-600"
    />
    <circle
      cx="8"
      cy="8"
      r={CIRCLE_R}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="text-green-500"
      strokeDasharray={CIRCLE_C}
      ref={(el) => {
        if (el) {
          requestAnimationFrame(() => {
            el.style.transition = `stroke-dashoffset ${DISMISS_MS}ms linear`
            el.style.strokeDashoffset = `${-CIRCLE_C}`
          })
        }
      }}
    />
  </svg>
)
